import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageBar from '../../../components/PageBar';
import PageLoading from '../../../components/PageLoading';
import { useAuth } from '../../../components/AuthContext';
import { useToast } from '../../../components/ToastContext';
import { loadDestinations, searchCatalog, placeOrder, URGENCY } from './orderJobs';

/*
  ORDERS -> PLACE AN ORDER, 4 October 2026. Door 5.

  **This is not a fifth way in. It is how the other four get closed.** His own
  reason: *"I want a section built that a rep could login to on hand and create an
  order for their location and when they click submit that job would go to my
  work queue for processing. I think if we can build that and i can eventually
  convert people to that process it would reduce the amount of doors work can
  enter through."*

  **It is the only door that arrives with nothing to interpret**, because the
  person picks from lists rather than writing prose. That makes it the easiest to
  build and the best proof that the queue works end to end.

  **Every job level can place one**, his answer of 4 October.

  -- Two things that are honestly missing, written down rather than hidden ----

  **A rep sees all 952 places, not just the ones they may order for.** He said a
  rep might have 15 to 50 accounts, and nothing in OnHand records who may order
  for what yet. Showing everything is wrong and showing a guess would be worse,
  so it shows everything and says so.

  **There is no short list of what a place usually orders.** He asked for one;
  it wants the par levels out of the ClaimSoft stock status export, which OnHand
  does not hold. So this searches all 356,074 catalog items instead.
*/

function Line({ line, onChange, onRemove }) {
  return (
    <div className="card" style={{ padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        <div className="rpnum">{line.item_as_given}</div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
          {line.description || 'No description'}
          {line.vendor_name ? '  -  ' + line.vendor_name : ''}
        </div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>How many</span>
        <input className="fld" type="number" min="1" step="1" style={{ width: 90, minHeight: 44 }}
          value={line.quantity}
          onChange={e => onChange({ ...line, quantity: e.target.value })}
          aria-label={'How many ' + line.item_as_given} />
      </label>
      <button type="button" className="btn btn-quiet" style={{ minHeight: 44 }}
        onClick={onRemove}>Remove</button>
    </div>
  );
}

export default function PlaceOrderPage() {
  const { profile } = useAuth();
  const toast = useToast();

  const [places, setPlaces] = useState(null);
  const [destinationId, setDestinationId] = useState('');
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState('');
  const [urgency, setUrgency] = useState('routine');
  const [customerPo, setCustomerPo] = useState('');

  const [query, setQuery] = useState('');
  const [found, setFound] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(null);
  const [placed, setPlaced] = useState(null);

  const load = useCallback(async () => {
    const { places: p, error } = await loadDestinations();
    /* A read that fails is not a list that is empty. */
    if (error) { setFailed(error.message); setPlaces([]); return; }
    setFailed(null);
    setPlaces(p);
  }, []);
  useEffect(() => { load(); }, [load]);

  /* The search waits for typing to stop. Three hundred thousand items is not
     something to ask about on every keystroke. */
  const timer = useRef(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (query.trim().length < 2) { setFound([]); setSearching(false); return undefined; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      const { items, error } = await searchCatalog(query);
      setFound(items);
      setSearchError(error ? error.message : null);
      setSearching(false);
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [query]);

  const addItem = useCallback(item => {
    setLines(prev => {
      const already = prev.findIndex(l => l.catalog_item_id === item.id);
      if (already >= 0) {
        const next = [...prev];
        next[already] = { ...next[already], quantity: Number(next[already].quantity || 0) + 1 };
        return next;
      }
      return [...prev, {
        key: item.id,
        catalog_item_id: item.id,
        vendor_id: item.vendor_id,
        vendor_name: item.vendor ? item.vendor.name : null,
        item_as_given: item.vendor_part_number,
        description: item.our_description || item.vendor_description || null,
        quantity: 1,
      }];
    });
    setQuery('');
    setFound([]);
  }, []);

  const total = useMemo(() => lines.reduce((n, l) => n + (Number(l.quantity) || 0), 0), [lines]);
  const place = places && places.find(p => p.id === destinationId);

  const submit = useCallback(async () => {
    setSaving(true);
    const res = await placeOrder({
      destinationId,
      lines,
      note,
      urgency,
      customerPo,
      requestedByName: profile?.full_name || null,
    });
    setSaving(false);
    if (!res.ok) { toast.error(res.error?.message || 'That did not save.'); return; }
    setPlaced(res.job);
    setLines([]); setNote(''); setCustomerPo(''); setUrgency('routine'); setDestinationId('');
    toast.success(res.job.reference + ' is in the queue');
  }, [destinationId, lines, note, urgency, customerPo, profile, toast]);

  if (places === null) return <><PageBar title="Place an Order" /><PageLoading /></>;

  return (
    <>
      <PageBar
        title="Place an Order"
        subtitle="Ask for stock. It goes into the ordering queue to be worked."
        meta={lines.length > 0 ? <span><b>{lines.length}</b> item{lines.length === 1 ? '' : 's'}, {total} in all</span> : null}
        actions={
          <button type="button" className="btn btn-primary" style={{ minHeight: 44 }}
            disabled={saving || !destinationId || lines.length === 0}
            onClick={submit}>
            {saving ? 'Sending...' : 'Send to the queue'}
          </button>
        }
      />

      {failed && (
        <div className="card" style={{ padding: 24, marginBottom: 16 }}>
          <p>OnHand could not read the list of places just now.</p>
          <p style={{ color: 'var(--text-secondary)' }}>{failed}</p>
          <button type="button" className="btn" onClick={load}>Try again</button>
        </div>
      )}

      {placed && (
        <div className="card" style={{ padding: '14px 18px', marginBottom: 16, background: 'var(--success-bg)' }}>
          <div style={{ color: 'var(--success-ink)', fontWeight: 700, fontSize: 13.5 }}>
            {placed.reference} is in the queue, {placed.lines} item{placed.lines === 1 ? '' : 's'}.
          </div>
          <div style={{ color: 'var(--success-ink)', fontSize: 12.5, marginTop: 4 }}>
            You will be told when it has been ordered. Start another below.
          </div>
        </div>
      )}

      <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 12 }}>
          Where is it going?
        </div>
        <select className="fld" style={{ maxWidth: 480, minHeight: 44 }}
          value={destinationId} onChange={e => setDestinationId(e.target.value)}
          aria-label="Where this order is going">
          <option value="">Pick a place</option>
          {places.map(p => (
            <option key={p.id} value={p.id}>
              {p.onhand_name}{p.region_name ? '  -  ' + p.region_name : ''}
            </option>
          ))}
        </select>
        {/* **Said out loud rather than hidden.** Nothing yet records who may
            order for which places, so everybody sees all of them. */}
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.55 }}>
          Every place is listed. OnHand does not yet know which ones you are allowed to order for.
        </div>
        {place && !place.account_edge_customer && (
          <div style={{ fontSize: 12.5, color: 'var(--warning-ink)', fontWeight: 600, marginTop: 8 }}>
            This place has no Account Edge customer set, so it cannot be ordered for until somebody sets one.
          </div>
        )}
      </div>

      <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>
          What do you need?
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Search by item number or description.
        </div>
        <input className="fld" style={{ maxWidth: 480, minHeight: 44 }}
          placeholder="Start typing an item number or a description..."
          value={query} onChange={e => setQuery(e.target.value)}
          aria-label="Search the catalog" />

        {searching && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 10 }}>Looking...</div>}

        {/* **A search that failed says so.** It used to leave "Looking..." on
            screen for ever, which reads as a slow search rather than a broken
            one. */}
        {!searching && searchError && (
          <div style={{ fontSize: 12.5, color: 'var(--critical-ink)', marginTop: 10, fontWeight: 600 }}>
            The catalog could not be searched just now. {searchError}
          </div>
        )}

        {!searching && found.length > 0 && (
          <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
            {found.map(i => (
              <button key={i.id} type="button" className="ua-row" style={{ minHeight: 44 }}
                onClick={() => addItem(i)}>
                <span className="ua-row-main">
                  <span className="ua-row-name">
                    <span className="rpnum">{i.vendor_part_number}</span>
                  </span>
                  <span className="ua-row-email">
                    {[i.our_description || i.vendor_description, i.vendor ? i.vendor.name : null]
                      .filter(Boolean).join('  -  ')}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}

        {!searching && !searchError && query.trim().length >= 2 && found.length === 0 && (
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 10 }}>
            Nothing in the catalog matches that.
          </div>
        )}
      </div>

      {lines.length > 0 && (
        <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
          {lines.map((l, i) => (
            <Line key={l.key}
              line={l}
              onChange={next => setLines(prev => prev.map((x, j) => (j === i ? next : x)))}
              onRemove={() => setLines(prev => prev.filter((x, j) => j !== i))} />
          ))}
        </div>
      )}

      <div className="card" style={{ padding: '18px 20px' }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 12 }}>
          Anything else?
        </div>
        <div className="wh-grid">
          <label>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 5 }}>How soon</div>
            <select className="fld" style={{ minHeight: 44 }} value={urgency}
              onChange={e => setUrgency(e.target.value)} aria-label="How soon this is needed">
              {Object.entries(URGENCY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 5 }}>{URGENCY[urgency].says}</div>
          </label>
          <label>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 5 }}>
              Their purchase order number
            </div>
            <input className="fld" style={{ minHeight: 44 }} value={customerPo}
              onChange={e => setCustomerPo(e.target.value)}
              placeholder="If a client gave you one" />
          </label>
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 5 }}>Note</div>
          <textarea className="fld" rows={3} value={note} onChange={e => setNote(e.target.value)}
            placeholder="Anything whoever works this should know" />
        </div>
      </div>
    </>
  );
}
